import classNames from "classnames";
import { useState } from "react";
import { Constants } from "../../../../../common/constants";
import {
	AttendeeRoleDataWithIDs,
	RoleOnRSVPAddTime,
} from "../../../../../common/sesh_types";
import { DashboardUtils } from "../../dashboard_utils";
import { SearchableDropdown } from "../searchable_dropdown";
import { useRoles } from "./hooks/use_roles";
import { InfoTooltip } from "./tooltips/info_tooltip";

export function AddRoleToAttendeesComponent(props: {
	value: AttendeeRoleDataWithIDs;
	setValue: (val: AttendeeRoleDataWithIDs) => void;
	event_duration_ms?: number;
	sesh_has_manage_roles: boolean;
	sesh_role_name: string;
	disabled?: boolean;
	has_no_roles_to_add?: boolean;
	placeholder?: string;
}) {
	const disabled_due_to_manage_roles = !props.sesh_has_manage_roles;
	let placeholder_text = props.placeholder;

	if (!placeholder_text) {
		if (disabled_due_to_manage_roles || props.disabled) {
			placeholder_text = "Disabled";
		} else {
			placeholder_text = "None";
		}
	}

	let disabled =
		disabled_due_to_manage_roles ||
		props.disabled ||
		props.has_no_roles_to_add;

	let duration_text = "Remove role on event end";

	const duration_label = (
		<label className="attendee-remove-on-end-label">
			<div className="flex items-center gap-2">
				<input
					type="checkbox"
					checked={props.value.remove_on_end}
					onChange={(e) =>
						props.setValue({
							...props.value,
							remove_on_end: e.target.checked,
						})
					}
					disabled={disabled}
				/>{" "}
				{duration_text}{" "}
				<InfoTooltip
					info={
						"Set event duration to determine when roles are removed. If no duration is set, roles will be removed one hour after the event starts."
					}
				/>
			</div>
		</label>
	);

	let label_element: JSX.Element;
	if (!props.disabled) {
		if (disabled_due_to_manage_roles) {
			label_element = (
				<p>
					<p className="error-note">
						<p className="">
							The <i>{props.sesh_role_name ?? "sesh"}</i> role
							requires the <b>Manage Roles</b> permission to use
							this feature.{" 	"}
						</p>
						<a
							className="manual-link"
							href={
								Constants.ATTENDEE_ROLES_HELP_LINK +
								"#invalid_permissions"
							}
							target="_blank"
						>
							Click here for more info
						</a>
					</p>
				</p>
			);
		} else if (props.has_no_roles_to_add) {
			label_element = (
				<p>
					<p className="error-note">
						No valid roles found. Ensure you have{" "}
						<b>Manage Roles</b> permissions.
					</p>
				</p>
			);
		}
	}

	return (
		<div className="flex flex-col gap-2">
			{label_element}
			<div className={`flex w-full flex-col gap-2`}>
				<div className="w-full">
					<RoleSelect
						value={props.value}
						setValue={props.setValue}
						placeholder_text={placeholder_text}
						disabled={disabled}
					/>
				</div>
				<div
					className={classNames({
						"input-disabled":
							(props.value?.role_ids.length ?? -1) === 0,
					})}
				>
					<div
						className={classNames(
							"flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-8",
							{
								"pointer-events-none":
									(props.value?.role_ids.length ?? -1) === 0,
							}
						)}
					>
						<div className="flex w-fit gap-2">
							<div className="">Add role on</div>
							<div>
								<select
									className="inline-dropdown"
									onChange={(val) =>
										props.setValue({
											...props.value,
											role_add_time: parseInt(
												val.target.value
											),
										})
									}
									value={props.value.role_add_time}
									disabled={disabled}
								>
									<option
										value={RoleOnRSVPAddTime.OnEventStart}
									>
										Event Start
									</option>
									<option value={RoleOnRSVPAddTime.OnRSVP}>
										User RSVP
									</option>
								</select>
							</div>
						</div>
						<div className="">{duration_label}</div>
					</div>
				</div>
			</div>
		</div>
	);
}

function RoleSelect(props: {
	value: AttendeeRoleDataWithIDs;
	setValue: (val: AttendeeRoleDataWithIDs) => void;
	disabled?: boolean;
	placeholder_text: string;
}) {
	const roles = useRoles();

	if (!roles) {
		return <DashboardUtils.RenderLoadingSelect />;
	}

	return (
		<SearchableDropdown
			handleChange={(val) =>
				props.setValue({
					...props.value,
					role_ids: val.map((v) => v.id),
				})
			}
			options={roles.filter(
				(val) => !val.is_everyone_or_here && !val.is_managed_role
			)}
			initial_mentions={roles.filter((val) =>
				props.value.role_ids.includes(val.id)
			)}
			ignore_invalid_permissions={true}
			check_can_sesh_and_user_add_role={true}
			disabled={props.disabled}
			placeholder={props.placeholder_text}
			is_single_select={true}
		/>
	);
}
